// useWakeLock — keep the screen awake while `active` is true (e.g. during a
// breathing session, so it doesn't dim/lock mid-exercise). Uses the standard
// Web Wake Lock API (web + iOS 16.4+ WKWebView); silently no-ops where it isn't
// supported, and re-acquires the lock when the tab/app returns to the foreground
// (the OS drops a screen wake lock whenever the page is hidden).
import { useEffect } from 'react';

type WakeLockSentinelLike = { release: () => Promise<void> };
type WakeLockNavigator = Navigator & {
  wakeLock?: { request: (type: 'screen') => Promise<WakeLockSentinelLike> };
};

export function useWakeLock(active: boolean): void {
  useEffect(() => {
    if (!active) return;
    let sentinel: WakeLockSentinelLike | null = null;
    let cancelled = false;

    const request = async () => {
      try {
        const nav = navigator as WakeLockNavigator;
        if (nav.wakeLock?.request) {
          sentinel = await nav.wakeLock.request('screen');
        }
      } catch {
        /* unsupported, denied, or not in a user gesture — fine, just no-op */
      }
    };

    void request();

    const onVisible = () => {
      if (document.visibilityState === 'visible' && !cancelled) void request();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      void sentinel?.release().catch(() => {});
    };
  }, [active]);
}
